import type {
    CreateExtensionArgument,
    CreateExtArgs,
    Extension,
    ExtensionAPI,
    DmApis,
    ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {BuilderStyle, CompRef} from '@wix/document-services-types'
import {DATA_TYPES} from '../../constants/constants'
import type {BuilderComponentsExtensionAPI} from './builderComponents'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {StylesExtensionApi} from '../style/style'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'

export interface BuilderStylePublicAPI extends ExtensionPublicAPI {
    components: {
        cssStyle: {
            update(
                componentPointer: CompRef,
                cssStyle: Partial<BuilderStyle>,
                shouldRemoveRelatedProperties?: boolean
            ): void
            get(componentPointer: CompRef): Partial<BuilderStyle> | undefined
            remove(componentPointer: CompRef, paths: string[]): void
        }
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): BuilderStyleExtensionAPI => {
        const {builderComponents, variants, style, cssStyle} = extensionAPI as BuilderComponentsExtensionAPI &
            VariantsExtensionAPI &
            StylesExtensionApi &
            CssStyleExtensionAPI

        const getCSSStyle = (componentPointer: CompRef): Partial<BuilderStyle> | undefined => {
            const styleItem = variants.getComponentItemConsideringVariants(componentPointer, DATA_TYPES.theme)
            if (styleItem) {
                return cssStyle.getPublicCSSStyle(styleItem)
            }
        }

        const buildDefaultStyleItem = (compPointer: CompRef): BuilderStyle => {
            const {style: componentStyleType} = builderComponents.getBuilderTypes(compPointer)
            return {
                type: componentStyleType
            }
        }
        const updateCSSStyle = (
            componentPointer: CompRef,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties = true
        ): void => {
            const styleItem =
                variants.getComponentItemConsideringVariants(componentPointer, DATA_TYPES.theme) ??
                buildDefaultStyleItem(componentPointer)

            const newStyleItem = cssStyle.getUpdatedCSSStyleItemForUpdate(
                styleItem,
                partialStyle,
                shouldRemoveRelatedProperties
            )
            if (newStyleItem.id && style.global.isGlobalStyle(newStyleItem.id)) {
                delete newStyleItem.id
            }

            variants.updateComponentDataConsideringVariants(componentPointer, newStyleItem, DATA_TYPES.theme)
        }

        const removeCSSStyleProperties = (componentPointer: CompRef, paths: string[]): void => {
            const styleItem = variants.getComponentItemConsideringVariants(componentPointer, DATA_TYPES.theme)
            if (!styleItem) return

            const itemAfterRemovalUpdates = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
            if (itemAfterRemovalUpdates) {
                variants.updateComponentDataConsideringVariants(
                    componentPointer,
                    itemAfterRemovalUpdates,
                    DATA_TYPES.theme
                )
            }
        }

        const updateCssProperties = (
            componentPointer: CompRef,
            partialCssProperties: Record<string, any>,
            shouldRemoveRelatedProperties = true
        ): void => {
            updateCSSStyle(componentPointer, {cssProperties: partialCssProperties}, shouldRemoveRelatedProperties)
        }

        const getCssProperties = (componentPointer: CompRef): BuilderStyle['cssProperties'] | undefined => {
            return getCSSStyle(componentPointer)?.cssProperties
        }

        const removeCssProperties = (componentPointer: CompRef, propertyKeys: string[]): void => {
            const paths = propertyKeys.map(key => `cssProperties.${key}`)
            removeCSSStyleProperties(componentPointer, paths)
        }

        const getCustomVariables = (componentPointer: CompRef): BuilderStyle['cssCustomProperties'] | undefined => {
            return getCSSStyle(componentPointer)?.cssCustomProperties
        }

        const updateCustomVariables = (
            componentPointer: CompRef,
            partialCustomVariables: Record<string, any>
        ): void => {
            updateCSSStyle(componentPointer, {cssCustomProperties: partialCustomVariables})
        }

        const removeCustomVariables = (componentPointer: CompRef, variableKeys: string[]): void => {
            const paths = variableKeys.map(key => `cssCustomProperties.${key}`)
            removeCSSStyleProperties(componentPointer, paths)
        }

        return {
            builderStyle: {
                component: {
                    getCSSStyle,
                    updateCSSStyle,
                    removeCSSStyleProperties,
                    updateCssProperties,
                    getCssProperties,
                    removeCssProperties,
                    updateCustomVariables,
                    getCustomVariables,
                    removeCustomVariables
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): BuilderStylePublicAPI => {
        const {builderStyle} = extensionAPI as BuilderStyleExtensionAPI

        const update = (
            componentPointer: CompRef,
            cssStyle: BuilderStyle,
            shouldRemoveRelatedProperties?: boolean
        ): void => {
            builderStyle.component.updateCSSStyle(componentPointer, cssStyle, shouldRemoveRelatedProperties)
        }

        const get = (componentPointer: CompRef) => {
            return builderStyle.component.getCSSStyle(componentPointer)
        }

        const remove = (componentPointer: CompRef, paths: string[]) => {
            builderStyle.component.removeCSSStyleProperties(componentPointer, paths)
        }

        return {
            components: {
                cssStyle: {
                    update,
                    get,
                    remove
                }
            }
        }
    }

    return {
        name: 'builderStyle',
        dependencies: new Set(['variants', 'builderComponents', 'cssStyle']),
        createExtensionAPI,
        createPublicAPI
    }
}

export interface BuilderStyleExtensionAPI extends ExtensionAPI {
    builderStyle: {
        component: {
            getCSSStyle(componentPointer: CompRef): Partial<BuilderStyle> | undefined
            updateCSSStyle(
                componentPointer: CompRef,
                partialStyle: Partial<BuilderStyle>,
                shouldRemoveRelatedProperties?: boolean
            ): void
            removeCSSStyleProperties(componentPointer: CompRef, paths: string[]): void
            updateCssProperties(
                componentPointer: CompRef,
                partialStyleItem: BuilderStyle['cssProperties'],
                shouldRemoveRelatedProperties?: boolean
            ): void
            getCssProperties(componentPointer: CompRef): BuilderStyle['cssProperties'] | undefined
            removeCssProperties(componentPointer: CompRef, propertyKeys: string[]): void
            updateCustomVariables(
                componentPointer: CompRef,
                partialCustomVariables: BuilderStyle['cssCustomProperties']
            ): void
            getCustomVariables(componentPointer: CompRef): BuilderStyle['cssCustomProperties'] | undefined
            removeCustomVariables(componentPointer: CompRef, variableKeys: string[]): void
        }
    }
}

export {createExtension}
